import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  StyleSheet,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { theme, useResponsive } from '@sg/garnish';

import {
  CustomizationContainer,
  CustomizationCTA,
  CustomizationDetails,
  CustomizationDetailsCompoundContainer,
} from '@order/features/customization';
import { useCart } from '@order/hooks';
import { IngredientModificationsTypes } from '@order/utils';

import { useProductDetailsScreenContext } from '../../../../ProductDetailsScreen.provider';
import { DressingProductImage } from '../../../DressingProductImage';
import { useCustomizationTelemetry } from '../ProductDetailsIngredientsView/hooks';
import { ProductDetailsContentNavigationHeader } from '../ProductDetailsNavigationHeader';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsDressingDetailsView = () => {
  const {
    product,
    lineItemId,
    addressId,
    restaurantSlug,
    isOutpost,
    quantity,
    dressingMode,
    selectedPortionsNumber,
    maxDressingsPortionsNumber,
    modifications,
    dressingsDetails,
    isBreadActive,
    isBreadAvailable,
    breadIngredientModification,
    startCustomization,
    setDressingModeToOnTheSide,
    setDressingModeToMixedIn,
    changeDressingWeight,
    changeDressingPortion,
    toggleBread,
    modifyCart,
  } = useProductDetailsScreenContext();

  const { isFetchingCart } = useCart();

  const { calories, netPriceChange, active } = modifications;

  const { currentBreakpoint, minWidth, match } = useResponsive();
  const { formatMessage } = useIntl();

  const headerStyle = match([null, [styles.headerSM], styles.headerLG]);
  const headerText = match([
    formatMessage(messages.dressingDetailsTitle),
    undefined,
  ]);

  // ─── Data ────────────────────────────────────────────────────────────

  const productCost = (product?.cost ?? 0) + netPriceChange;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isEditProductMode = lineItemId !== undefined;

  // delivery and outpost orders cannot have mixed-in dressing
  const isDelivery = Boolean(addressId);

  const hasMixableIngredient = active?.some(
    (modification) => modification.mixable,
  );
  const hasNoSelectedDressing = dressingsDetails.length === 0;
  const hasGreenBase = active?.some(
    (modification) =>
      !modification.isGrain &&
      modification.kind === IngredientModificationsTypes.Bases,
  );

  const isDressingModeChangeNotAllowed =
    hasNoSelectedDressing ||
    isDelivery ||
    isOutpost ||
    !hasMixableIngredient ||
    !hasGreenBase;

  const isBreadOutOfStock = breadIngredientModification?.outOfStock === true;

  // ─── Styles ──────────────────────────────────────────────────────────

  const selectedIngredientsScrollSV = useSharedValue(0);

  const updateIngredientsScrollSV = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      // eslint-disable-next-line functional/immutable-data
      selectedIngredientsScrollSV.value = event.nativeEvent.contentOffset.y;
    },
    [selectedIngredientsScrollSV],
  );

  // ─── Telemetry ───────────────────────────────────────────────────────

  const {
    trackDressingOnTheSideTapped,
    trackDressingMixedInTapped,
    trackDressingBreadTapped,
  } = useCustomizationTelemetry();

  const selectOnTheSide = useCallback(() => {
    setDressingModeToOnTheSide();
    trackDressingOnTheSideTapped();
  }, [setDressingModeToOnTheSide, trackDressingOnTheSideTapped]);

  const selectMixedIn = useCallback(() => {
    setDressingModeToMixedIn();
    trackDressingMixedInTapped();
  }, [setDressingModeToMixedIn, trackDressingMixedInTapped]);

  const selectBread = useCallback(() => {
    toggleBread();
    trackDressingBreadTapped();
  }, [toggleBread, trackDressingBreadTapped]);

  // ─────────────────────────────────────────────────────────────────────

  if (!product) return null;

  return (
    <CustomizationContainer>
      <CustomizationDetailsCompoundContainer.Aside isFluid>
        <CustomizationDetailsCompoundContainer.ScrollView
          style={styles.container}
          stickyHeaderIndices={[0]}
          onScroll={updateIngredientsScrollSV}
          scrollEventThrottle={16}
          bounces={false}
        >
          <ProductDetailsContentNavigationHeader
            spacing="standard"
            style={headerStyle}
            scrollOffsetSV={selectedIngredientsScrollSV}
            restaurantSlug={restaurantSlug}
            addressId={addressId}
            text={headerText}
            onBackButtonPress={startCustomization}
          />

          <CustomizationDetails.Container>
            <CustomizationDetails.DressingMode
              dressingMode={dressingMode}
              maxDressingsPortions={maxDressingsPortionsNumber}
              hasNoSelectedDressing={hasNoSelectedDressing}
              isDressingModeChangeNotAllowed={isDressingModeChangeNotAllowed}
              isDelivery={isDelivery}
              isOutpost={isOutpost}
              isDisabled={isFetchingCart}
              setDressingModeToOnTheSide={selectOnTheSide}
              setDressingModeToMixedIn={selectMixedIn}
              onAddDressing={startCustomization}
            />

            {dressingsDetails.map((dressingDetails) => {
              const { ingredientModification } = dressingDetails;
              const isMixable = ingredientModification.mixable;
              const remainingPortions =
                maxDressingsPortionsNumber - selectedPortionsNumber;

              if (dressingMode === 'onTheSide' || !isMixable) {
                return (
                  <CustomizationDetails.DressingPortion
                    key={ingredientModification.ingredient.id}
                    ingredientModification={ingredientModification}
                    activeDressingsDetails={dressingsDetails}
                    maxPortions={maxDressingsPortionsNumber}
                    remainingPortions={remainingPortions}
                    isDisabled={isFetchingCart}
                    setDressingPortion={changeDressingPortion}
                  />
                );
              }

              return (
                <CustomizationDetails.DressingWeight
                  key={ingredientModification.ingredient.id}
                  ingredientModification={ingredientModification}
                  activeDressingsDetails={dressingsDetails}
                  isDisabled={isFetchingCart}
                  setDressingWeight={changeDressingWeight}
                />
              );
            })}

            {isBreadAvailable ? (
              <CustomizationDetails.BreadToggle
                isActive={isBreadActive}
                isDisabled={isBreadOutOfStock || isFetchingCart}
                breadCalories={breadIngredientModification?.calories}
                onChange={selectBread}
              />
            ) : null}
          </CustomizationDetails.Container>
        </CustomizationDetailsCompoundContainer.ScrollView>

        {minWidth.isSM ? (
          <CustomizationCTA
            variation={isEditProductMode ? 'update' : 'add-to-bag'}
            cost={productCost}
            quantity={quantity}
            calories={calories}
            disabled={isFetchingCart}
            onPress={modifyCart}
          />
        ) : null}
      </CustomizationDetailsCompoundContainer.Aside>

      {minWidth.isSM ? <DressingProductImage product={product} /> : null}

      {currentBreakpoint.isXS ? (
        <CustomizationCTA
          variation={isEditProductMode ? 'update' : 'add-to-bag'}
          disabled={isFetchingCart}
          cost={productCost}
          quantity={quantity}
          calories={calories}
          onPress={modifyCart}
        />
      ) : null}
    </CustomizationContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dressingDetailsTitle: {
    defaultMessage: 'Dressing details',
    description: 'Customization | Title | Dressing details',
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingBottom: theme.spacing['16'],
  },
  headerSM: {
    paddingHorizontal: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
  },
  headerLG: {
    paddingHorizontal: theme.spacing['4'],
    paddingVertical: theme.spacing['1'],
  },
});
